import React, { useCallback, useEffect, useRef, useState } from "react";
import Reveal from "../Reveal";
import SectionIntro from "./SectionIntro";
import useScrollProgress from "../../hooks/useScrollProgress";
import { STORY } from "../../data/about";

/**
 * The company's story, travelled through rather than listed.
 *
 * On a wide screen the section pins itself and the chapters move sideways as
 * the page scrolls, so going forward in the page is going forward in time. The
 * section is made exactly as tall as the rail is wide, which makes the mapping
 * 1:1 — a pixel of scroll is a pixel of travel, and the rail can never run out
 * before the pin releases or keep going after it.
 *
 * Below `lg`, and whenever reduced motion is asked for, none of that happens:
 * the same chapters stack vertically and reveal normally. Hijacking scroll on
 * a phone is how this pattern earns its bad reputation, and a pinned section
 * is unusable to anyone who needs the page to just scroll.
 */

function Chapter({ chapter, index, horizontal }) {
  return (
    <article className={horizontal ? "w-[76vw] shrink-0 sm:w-[25rem]" : ""}>
      <div className="flex items-baseline gap-4">
        {/* The year is the loudest thing in the panel — it is what the eye
            uses to place itself in the sequence. */}
        <span className="mw-display text-[#1E1B16]">{chapter.year}</span>
        <span className="mw-eyebrow text-[#6A0DAD]">
          {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      <div className="mt-6 h-px w-full bg-[#1E1B16]/15" aria-hidden="true" />

      <h3 className="mt-6 text-xl font-semibold tracking-tight text-[#1E1B16] sm:text-2xl">
        {chapter.title}
      </h3>
      <p className="mw-lede mt-3 max-w-md text-[#4D4B46]">{chapter.body}</p>
    </article>
  );
}

function StoryScroll() {
  const sectionRef = useRef(null);
  const railRef = useRef(null);
  const barRef = useRef(null);

  const [horizontal, setHorizontal] = useState(false);
  const [shift, setShift] = useState(0);

  /* Whether the pinned treatment applies at all. Re-checked on resize and on a
     change of motion preference, so rotating a tablet or turning reduced
     motion on mid-visit lands on the right layout instead of a broken one. */
  useEffect(() => {
    const wide = window.matchMedia("(min-width: 1024px)");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");

    const check = () => setHorizontal(wide.matches && !still.matches);

    check();
    wide.addEventListener("change", check);
    still.addEventListener("change", check);

    return () => {
      wide.removeEventListener("change", check);
      still.removeEventListener("change", check);
    };
  }, []);

  /* How far the rail has to travel: everything past the first screenful. */
  useEffect(() => {
    if (!horizontal) {
      setShift(0);
      return;
    }

    const measure = () => {
      const rail = railRef.current;
      if (!rail) return;
      setShift(Math.max(0, rail.scrollWidth - window.innerWidth));
    };

    measure();

    // Fonts landing after first paint change the rail's width, and a stale
    // measurement here shows up as a story that stops short of its last
    // chapter — so re-measure once the layout has actually settled.
    const settle = setTimeout(measure, 400);
    window.addEventListener("resize", measure);

    return () => {
      clearTimeout(settle);
      window.removeEventListener("resize", measure);
    };
  }, [horizontal]);

  const onProgress = useCallback(
    (progress) => {
      const rail = railRef.current;
      const bar = barRef.current;
      // `shift` is read through the render closure, which the hook refreshes
      // every render — so a resize is picked up on the next frame.
      if (rail) {
        rail.style.setProperty("--mw-rail-x", `${(-progress * shift).toFixed(2)}px`);
      }
      if (bar) bar.style.transform = `scaleX(${progress.toFixed(4)})`;
    },
    [shift]
  );

  useScrollProgress({
    ref: sectionRef,
    onProgress,
    from: 0,
    to: 1,
    disabled: !horizontal
  });

  const intro = (
    <SectionIntro index={STORY.index} label={STORY.label} title={STORY.title} />
  );

  /* ----------------------------------------------------------- stacked */

  if (!horizontal) {
    return (
      <section
        id="story"
        className="scroll-mt-24 border-y border-[#EDE4D8] bg-[#F3EFE6] py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>{intro}</Reveal>

          <div className="mt-14 space-y-14">
            {STORY.chapters.map((chapter, i) => (
              <Reveal key={chapter.year} delay={i * 60}>
                <Chapter chapter={chapter} index={i} horizontal={false} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ------------------------------------------------------------ pinned */

  return (
    <section
      id="story"
      ref={sectionRef}
      className="relative scroll-mt-24 border-y border-[#EDE4D8] bg-[#F3EFE6]"
      style={{ height: `calc(100vh + ${shift}px)` }}
    >
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <div className="mx-auto w-full max-w-7xl px-6 pt-28 md:px-10">
          <div className="flex items-end justify-between gap-10">
            {intro}

            {/* Where you are in the story. A thin rule rather than dots: it
                reads as a timeline, which is what it is measuring. */}
            <div className="w-52 shrink-0 pb-2">
              <div className="h-px w-full bg-[#1E1B16]/15">
                <div
                  ref={barRef}
                  className="h-px w-full origin-left scale-x-0 bg-[#6A0DAD]"
                />
              </div>
              <div className="mt-3 flex justify-between">
                <span className="mw-eyebrow text-[#4D4B46]">
                  {STORY.chapters[0].year}
                </span>
                <span className="mw-eyebrow text-[#4D4B46]">
                  {STORY.chapters[STORY.chapters.length - 1].year}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* The rail. It runs to the viewport edge on purpose — a rail that
            stopped at the container margin would look like it had simply been
            cut off rather than continuing past the screen. */}
        <div className="flex flex-1 items-center">
          <div
            ref={railRef}
            className="mw-rail flex gap-16 px-6 md:px-10 xl:gap-20"
            style={{ paddingRight: "18vw" }}
          >
            {STORY.chapters.map((chapter, i) => (
              <Chapter key={chapter.year} chapter={chapter} index={i} horizontal />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default StoryScroll;
